import React, { useState, useEffect } from 'react';
import { ArrowRight, FileText } from 'lucide-react';
import PaymentModal from '../billing/PaymentModal';

interface QuoteItem {
    name: string;
    qty: number;
    price: number;
}

interface Quote {
    id: string;
    clientName: string;
    items: QuoteItem[];
    total: number;
    reference: string;
    status: string;
}

// Mock Quote Fetcher (Replace with real API/DB fetch)
const fetchQuote = async (quoteId: string): Promise<Quote> => {
    // In reality, fetch from API or search locally
    return {
        id: quoteId,
        clientName: 'Acme Corp',
        items: [
            { name: 'BizBuilder Bundle', qty: 1, price: 4500 },
            { name: 'Setup Fee', qty: 1, price: 500 }
        ],
        total: 5000,
        reference: quoteId.replace('Q-', 'REF-'),
        status: 'Pending'
    };
};

const QuoteView = ({ quoteId }) => {
    const [quote, setQuote] = useState<Quote | null>(null);
    const [loading, setLoading] = useState(true);
    const [showPayModal, setShowPayModal] = useState(false);

    useEffect(() => {
        if (quoteId) {
            fetchQuote(quoteId).then(q => {
                setQuote(q);
                setLoading(false);
            });
        }
    }, [quoteId]);

    if (loading) {return <div className="flex h-64 items-center justify-center text-slate-400 font-bold">Loading Quote...</div>;}
    if (!quote) {return <div className="flex h-64 items-center justify-center text-red-400 font-bold">Quote Not Found</div>;}

    const handleSuccess = () => {
        alert("Thank you! Payment received. We are processing your order.");
        // Redirect to Onboarding or Dashboard
    };

    return (
        <div className="max-w-2xl mx-auto mt-8">
            <div className="bg-white rounded-[2.5rem] shadow-xl shadow-slate-200 overflow-hidden border border-slate-100">
                <div className="bg-slate-900 p-8 text-white relative overflow-hidden">
                    <div className="relative z-10">
                        <p className="text-blue-400 font-bold uppercase tracking-widest text-xs mb-2">Quote for {quote.clientName}</p>
                        <h1 className="text-3xl font-black tracking-tight mb-1">R {quote.total.toLocaleString()}</h1>
                        <p className="text-slate-400 text-sm font-mono">{quote.id}</p>
                    </div>
                    {/* Background decoration */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600 rounded-full blur-[100px] opacity-20 -mr-20 -mt-20 pointer-events-none" />
                </div>

                <div className="p-8">
                    <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                        <FileText size={16} className="text-blue-600" /> Line Items
                    </h3>
                    <div className="space-y-3 mb-8">
                        {quote.items.map((item, i) => (
                            <div key={i} className="flex justify-between items-center py-3 border-b border-slate-50 last:border-0">
                                <div>
                                    <p className="font-bold text-slate-700">{item.name}</p>
                                    <p className="text-xs text-slate-400">Qty: {item.qty}</p>
                                </div>
                                <p className="font-mono font-medium text-slate-600">
                                    {(item.price * item.qty).toLocaleString()}
                                </p>
                            </div>
                        ))}
                    </div>

                    <button
                        onClick={() => setShowPayModal(true)}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-bold uppercase tracking-widest flex items-center justify-center gap-2 transition-all shadow-lg shadow-blue-200 active:scale-95"
                    >
                        Proceed to Payment <ArrowRight size={20} />
                    </button>

                    <p className="text-center text-xs text-slate-400 mt-4 font-medium">
                        Secure payment via PayFast or Instant EFT.
                    </p>
                </div>
            </div>

            <PaymentModal
                isOpen={showPayModal}
                onClose={() => setShowPayModal(false)}
                invoice={quote}
                onSuccess={handleSuccess}
            />
        </div>
    );
};

export default QuoteView;
